import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useBrand } from '@/shared/brand/useBrand';
import { BrandLogo } from '@/shared/components/BrandLogo';
import { LocaleSwitcher } from '@/shared/i18n/LocaleSwitcher';
import { useI18n } from '@/shared/i18n/useT';

/**
 * "Botón de Baja de Servicio" required by Disposición 954/2025. Reachable
 * without signing in and linked from the first screen of both brands, because
 * the norm is precisely about not putting obstacles in front of leaving.
 *
 * It records a request and returns a management code; it does not delete
 * anything by itself. Self-service deletion does not exist yet, and inventing a
 * cascade across organizations, documents and bookings here would be a far
 * larger change than the norm asks for.
 */
export function CancellationPage() {
  const { locale, t } = useI18n();
  const brand = useBrand();
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [code, setCode] = useState('');
  const [emailed, setEmailed] = useState(false);

  const title = t('legal.cancellation.title');

  useEffect(() => {
    const previous = document.title;
    document.title = `${title} · ${brand.name}`;
    return () => {
      document.title = previous;
    };
  }, [brand.name, title]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (state === 'sending') return;
    setState('sending');
    try {
      const response = await fetch('/api/cancellations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, reason, kind: 'baja', brand: brand.id, locale }),
      });
      if (!response.ok) throw new Error('request_failed');
      const payload = (await response.json()) as { code: string; emailed: boolean };
      setCode(payload.code);
      setEmailed(payload.emailed);
      setState('done');
    } catch {
      setState('error');
    }
  }

  const homePath = brand.id === 'numtable' ? '/signin' : '/book';

  return (
    <div className="-mx-3 min-h-[calc(100dvh-3rem)] bg-surface sm:-mx-6">
      <header className="border-b border-border bg-surface-2">
        <div className="mx-auto flex min-h-header max-w-4xl items-center justify-between gap-4 px-3 sm:px-6">
          <Link to={homePath} className="tap-target flex items-center rounded-token" aria-label={brand.name}>
            <BrandLogo className="h-6 w-auto sm:h-7" />
          </Link>
          <LocaleSwitcher compact />
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-3 py-10 sm:px-6 sm:py-14">
        <article className="max-w-2xl">
          <h1 className="text-3xl font-semibold tracking-tight text-fg sm:text-4xl">{title}</h1>
          <p className="mt-4 text-base leading-7 text-muted">{t('legal.cancellation.intro')}</p>

          {state === 'done' ? (
            <div className="mt-8 rounded-token border border-border bg-surface-2 p-5 sm:p-6">
              <h2 className="text-xl font-semibold text-fg">{t('legal.cancellation.doneTitle')}</h2>
              <p className="mt-3 text-base leading-7 text-muted">{t('legal.cancellation.doneBody')}</p>
              <p className="mt-4 text-sm font-medium text-fg">{t('legal.cancellation.codeLabel')}</p>
              <p className="mt-1 font-mono text-2xl tracking-widest text-accent">{code}</p>
              <p className="mt-4 text-sm leading-6 text-muted">
                {emailed ? t('legal.cancellation.emailSent') : t('legal.cancellation.emailFailed')}
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-5 rounded-token border border-border bg-surface-2 p-5 sm:p-6">
              <div>
                <label htmlFor="cancellation-email" className="block text-sm font-medium text-fg">
                  {t('legal.cancellation.emailLabel')}
                </label>
                <input
                  id="cancellation-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="mt-2 min-h-11 w-full rounded-token border border-border bg-surface px-3 text-base text-fg"
                />
              </div>
              <div>
                <label htmlFor="cancellation-reason" className="block text-sm font-medium text-fg">
                  {t('legal.cancellation.reasonLabel')}
                </label>
                <textarea
                  id="cancellation-reason"
                  rows={4}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  className="mt-2 w-full rounded-token border border-border bg-surface p-3 text-base text-fg"
                />
                <p className="mt-2 text-sm text-muted">{t('legal.cancellation.reasonHint')}</p>
              </div>
              {state === 'error' ? (
                <p role="alert" className="text-sm font-medium text-danger">
                  {t('legal.cancellation.error')}
                </p>
              ) : null}
              <button
                type="submit"
                disabled={state === 'sending'}
                className="min-h-11 w-full rounded-token bg-accent px-4 font-medium text-on-accent hover:bg-accent-hover disabled:opacity-60 sm:w-auto"
              >
                {state === 'sending' ? t('legal.cancellation.submitting') : t('legal.cancellation.submit')}
              </button>
            </form>
          )}

          <p className="mt-8 text-sm leading-6 text-muted">{t('legal.cancellation.footnote')}</p>
          <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-sm">
            <Link to="/terms" className="text-accent hover:text-accent-hover">
              {t('legal.terms.shortTitle')}
            </Link>
            <Link to="/privacy" className="text-accent hover:text-accent-hover">
              {t('legal.privacy.shortTitle')}
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
}
